import { Component } from '@theme/component';
import { prefersReducedMotion } from '@theme/utilities';

/**
 * @typedef {object} HoverVideoRefs
 * @property {HTMLVideoElement} [video] - The clip to play.
 */

/**
 * A clip that plays while it is being looked at.
 *
 * On a pointer, that means hover: playback starts on enter and pauses on leave,
 * keeping its position so coming back resumes rather than restarts. Where there
 * is no hover — a phone, a tablet — that reading of "being looked at" is useless,
 * so touch devices fall back to visibility instead, and the clip plays while it
 * holds the screen.
 *
 * Nothing is downloaded until one of those two things happens. The markup ships
 * `preload="none"`, so a section of these costs a poster frame each until a
 * visitor shows interest in one of them; `load()` on first play is what starts
 * the fetch.
 *
 * `pointerenter` and `pointerleave` do not bubble, so these are bound to the
 * element rather than declared with `on:` in the markup, which relies on
 * delegation from the document.
 *
 * @extends {Component<HoverVideoRefs>}
 */
class HoverVideoComponent extends Component {
  /** @type {IntersectionObserver | undefined} */
  #observer;

  /** @type {boolean} */
  #loaded = false;

  connectedCallback() {
    super.connectedCallback();

    if (!this.refs.video) return;

    if (matchMedia('(hover: hover)').matches) {
      this.addEventListener('pointerenter', this.play);
      this.addEventListener('pointerleave', this.pause);
      // Keyboard visitors reach the card through its links, so the same
      // treatment follows focus in and out of it.
      this.addEventListener('focusin', this.play);
      this.addEventListener('focusout', this.pause);
    } else {
      this.#observer = new IntersectionObserver(this.#onVisibilityChange, { threshold: 0.6 });
      this.#observer.observe(this);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.removeEventListener('pointerenter', this.play);
    this.removeEventListener('pointerleave', this.pause);
    this.removeEventListener('focusin', this.play);
    this.removeEventListener('focusout', this.pause);
    this.#observer?.disconnect();
  }

  /** Starts the clip, fetching it first if this is the first time. */
  play = () => {
    const { video } = this.refs;
    if (!video || prefersReducedMotion()) return;

    if (!this.#loaded) {
      video.load();
      this.#loaded = true;
    }

    // Rejects when the browser declines to autoplay. Nothing is owed to the
    // visitor in that case: the poster is already showing what they would see.
    video.play().then(
      () => this.toggleAttribute('data-playing', true),
      () => this.toggleAttribute('data-playing', false)
    );
  };

  /** Pauses where it stands, so coming back resumes rather than restarts. */
  pause = () => {
    const { video } = this.refs;
    if (!video) return;

    video.pause();
    this.toggleAttribute('data-playing', false);
  };

  /** @param {IntersectionObserverEntry[]} entries */
  #onVisibilityChange = ([entry]) => {
    if (entry?.isIntersecting) this.play();
    else this.pause();
  };
}

if (!customElements.get('hover-video')) {
  customElements.define('hover-video', HoverVideoComponent);
}
